import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Stack,
  TextField,
  Dropdown,
  IDropdownOption,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Text,
  IconButton,
  Spinner,
} from '@fluentui/react';
import { IMengeneinheit } from '../../shared/models';
import { centeredModalStyles, modalContentStyles } from '../styles/dialogs';
import { artikelApi, mengeneinheitenApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

interface IArtikelAnlegenDialogProps {
  isOpen: boolean;
  onDismiss: () => void;
  onSuccess: (artikel: any) => void;
  initialData?: {
    Artikelname?: string;
    Artikelnummer?: string;
    BCNummer?: string;
  };
}

export const ArtikelAnlegenDialog: React.FC<IArtikelAnlegenDialogProps> = ({
  isOpen,
  onDismiss,
  onSuccess,
  initialData,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    Artikelname: initialData?.Artikelname || '',
    Artikelnummer: initialData?.Artikelnummer || '',
    BCNummer: initialData?.BCNummer || '',
    Artikelbeschreibung: '',
    Artikelhyperlink: '',
    Einzelpreis: 0,
    Mengeneinheit: 'Stk',
  });
  const [mengeneinheiten, setMengeneinheiten] = useState<IMengeneinheit[]>([]);
  const [einzelpreisInput, setEinzelpreisInput] = useState<string>('0');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadMengeneinheiten();
    }
  }, [isOpen]);

  const loadMengeneinheiten = async () => {
    try {
      const data = await mengeneinheitenApi.getAll();
      setMengeneinheiten(data);
    } catch (err) {
      console.error('Fehler beim Laden der Mengeneinheiten:', err);
    }
  };

  const mengeneinheitenOptions: IDropdownOption[] = useMemo(() => {
    return mengeneinheiten.map((me) => ({
      key: me.Kurzform || me.Einheit,
      text: `${me.Einheit} (${me.Kurzform})`,
    }));
  }, [mengeneinheiten]);

  const parseDecimal = (value: string): number => {
    if (!value || value.trim() === '') return 0;
    const normalized = value.replace(',', '.');
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? 0 : parsed;
  };

  const handleSave = async () => {
    // Validierung
    if (!formData.Artikelname) {
      setError('Artikelname ist erforderlich');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Erstelle neuen Artikel
      const newArtikel = await artikelApi.create({
        ...formData,
        AuthorEmail: user?.email,
        AuthorName: user?.displayName,
      });

      onSuccess(newArtikel);
      onDismiss();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Anlegen des Artikels');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onDismiss}
      isBlocking={true}
      styles={centeredModalStyles}
    >
      {/* Header */}
      <div style={modalContentStyles.header}>
        <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
          Neuen Artikel anlegen
        </Text>
        <IconButton
          iconProps={{ iconName: 'Cancel' }}
          ariaLabel="Schließen"
          onClick={onDismiss}
          disabled={saving}
        />
      </div>

      {/* Body */}
      <div style={modalContentStyles.body}>
        <Stack tokens={{ childrenGap: 16 }}>
          {error && (
            <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
              {error}
            </MessageBar>
          )}

          <TextField
            label="Artikelname"
            placeholder="Name des Artikels"
            value={formData.Artikelname}
            onChange={(_, value) =>
              setFormData((prev) => ({ ...prev, Artikelname: value || '' }))
            }
            required
            disabled={saving}
          />

          <Stack horizontal tokens={{ childrenGap: 16 }}>
            <TextField
              label="Artikelnummer"
              placeholder="Art.Nr"
              value={formData.Artikelnummer}
              onChange={(_, value) =>
                setFormData((prev) => ({ ...prev, Artikelnummer: value || '' }))
              }
              disabled={saving}
              styles={{ root: { width: '50%' } }}
            />
            <TextField
              label="Bögra-Nr."
              placeholder="Bögra Artikelnummer"
              value={formData.BCNummer}
              onChange={(_, value) =>
                setFormData((prev) => ({ ...prev, BCNummer: value || '' }))
              }
              disabled={saving}
              styles={{ root: { width: '50%' } }}
            />
          </Stack>

          <TextField
            label="Artikelbeschreibung"
            placeholder="Beschreibung des Artikels"
            value={formData.Artikelbeschreibung}
            onChange={(_, value) =>
              setFormData((prev) => ({ ...prev, Artikelbeschreibung: value || '' }))
            }
            disabled={saving}
            multiline
            rows={3}
          />

          <TextField
            label="Link zum Artikel"
            placeholder="https://..."
            value={formData.Artikelhyperlink}
            onChange={(_, value) =>
              setFormData((prev) => ({ ...prev, Artikelhyperlink: value || '' }))
            }
            disabled={saving}
          />

          <Stack horizontal tokens={{ childrenGap: 16 }}>
            <TextField
              label="Einzelpreis (€)"
              type="text"
              placeholder="z.B. 12,50"
              value={einzelpreisInput}
              onChange={(_, value) => {
                setEinzelpreisInput(value || '');
                const preis = parseDecimal(value || '0');
                setFormData((prev) => ({ ...prev, Einzelpreis: preis }));
              }}
              disabled={saving}
              styles={{ root: { width: '50%' } }}
            />
            <Dropdown
              label="Mengeneinheit"
              placeholder="Mengeneinheit wählen"
              selectedKey={formData.Mengeneinheit || 'Stk'}
              onChange={(_, option) =>
                setFormData((prev) => ({ ...prev, Mengeneinheit: option?.key as string || 'Stk' }))
              }
              options={mengeneinheitenOptions}
              disabled={saving}
              styles={{ root: { width: '50%' } }}
            />
          </Stack>

          {saving && (
            <Stack horizontalAlign="center" tokens={{ padding: 20 }}>
              <Spinner label="Lege Artikel an..." />
            </Stack>
          )}
        </Stack>
      </div>

      {/* Footer */}
      <div style={modalContentStyles.footer}>
        <DefaultButton text="Abbrechen" onClick={onDismiss} disabled={saving} />
        <PrimaryButton text="Anlegen" onClick={handleSave} disabled={saving} />
      </div>
    </Modal>
  );
};
